import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import { useTrackedForm, useUnsaved } from "../unsaved";

// How far (km) a delivery/pickup can be from its hub before it's rejected -
// bandSizeKm x maxBands is the cap PricingUtils.deliveryBands() enforces on
// every quote()/prebook call, and what the Register page's map picker
// (Components/DeliveryLocationPicker) draws its radius circle from
// (/admin/booking/delivery-limits). Was only a raw schema default
// (5km x 3 bands = 15km) with no UI to change it until now - deep-dive
// report punch-list #7.
const emptyForm = { bandSizeKm: "5", maxBands: "3" };

const DeliveryRadiusTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { form, setForm, loadForm, dirty, discard } = useTrackedForm(emptyForm);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: "/admin/business/site/delivery-radius/view" });
        if (response?.success) {
            const data = response?.data?.data || {};
            loadForm({
                bandSizeKm: String(data.bandSizeKm ?? 5),
                maxBands: String(data.maxBands ?? 3),
            });
        }
        setLoading(false);
    }, [loadForm]);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const bandSizeKm = Number(form.bandSizeKm);
    const maxBands = Number(form.maxBands);
    const maxKm = Number.isFinite(bandSizeKm) && Number.isFinite(maxBands) ? bandSizeKm * maxBands : null;

    const onSave = async () => {
        if (!Number.isFinite(bandSizeKm) || bandSizeKm <= 0) { toast.error("Band size must be greater than 0"); return; }
        if (!Number.isInteger(maxBands) || maxBands < 1) { toast.error("Max bands must be a whole number of 1 or more"); return; }
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: "/admin/business/site/delivery-radius/update",
            data: { bandSizeKm, maxBands },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Delivery radius updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update delivery radius"));
        }
        setSaving(false);
    };

    useUnsaved("delivery-radius", dirty, onSave, discard);

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={38} className="mb-3" />
                <Skeleton height={38} className="mb-3" />
            </div>
        );
    }

    return (
        <div className="settings-card">
            <h5 className="fw-semibold mb-3">Delivery Radius</h5>
            <p className="text-muted small mb-4">
                How far a delivery or pickup can be from its car's hub. Distance is billed in whole bands - a booking that's just over one band's
                size is charged for the next full band. The Registration page's map picker rejects any point past the total radius below.
            </p>

            <div className="row g-4">
                <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="delivery-radius-bandSizeKm">Band size (km)</label>
                    <input
                        id="delivery-radius-bandSizeKm"
                        type="number"
                        min="0.1"
                        step="0.5"
                        name="bandSizeKm"
                        className="form-control"
                        value={form.bandSizeKm}
                        onChange={handleChange}
                    />
                    <div className="form-text">Distance rounds up to the next full band of this size.</div>
                </div>
                <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="delivery-radius-maxBands">Max bands</label>
                    <input
                        id="delivery-radius-maxBands"
                        type="number"
                        min="1"
                        step="1"
                        name="maxBands"
                        className="form-control"
                        value={form.maxBands}
                        onChange={handleChange}
                    />
                    <div className="form-text">How many bands away a delivery/pickup can be before it's rejected.</div>
                </div>
                <div className="col-12 col-md-4">
                    <label className="form-label">Total radius</label>
                    <input type="text" className="form-control" disabled value={maxKm !== null ? `${maxKm} km` : "-"} />
                    <div className="form-text">Band size &times; max bands.</div>
                </div>
            </div>

            <div className="settings-card-actions">
                <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save changes"}
                </button>
            </div>
        </div>
    );
};

export default DeliveryRadiusTab;
